import { useState } from "react";
import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  BookOpen,
  Sparkles,
  TrendingUp,
  Users,
  Clock,
  Zap,
  Target,
  Award
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { DashboardLayout } from "@/components/DashboardLayout";
import { useAuth } from "@/hooks/use-auth";
import { apiService } from "@/services/api";
import { ModelSelector } from "@/components/ModelSelector";
import ProjectCard from "@/components/ProjectCard";
import { cacheManager } from "@/utils/cacheUtils";

interface Project {
  id: number;
  uuid?: string;
  title: string;
  bookType: string;
  targetWordCount: number;
  status: string;
  updatedAt: string;
  createdAt: string;
  projectData?: any;
  progress?: {
      overall: number;
      blueprint: number;
      chapters: number;
      metadata: number;
      export: number;
    };
}


interface CreditStats {
  balance: number;
  totalEarned: number;
  totalSpent: number;
}

interface UsageStats {
  totalTasks: number;
  thisMonthTasks: number;
}

export default function Dashboard() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [storyIdea, setStoryIdea] = useState("");
  const [selectedModel, setSelectedModel] = useState<any>(null);

  const { data: creditStats } = useQuery<CreditStats>({ queryKey: ["/api/credits/stats"], enabled: !!user });
  const { data: usageStats } = useQuery<UsageStats>({ queryKey: ["/api/usage/stats"], enabled: !!user });
  const { data: fetchedProjects, isLoading: projectsLoading } = useQuery<Project[]>({
    queryKey: ["/api/story-projects/summaries"],
    enabled: !!user,
  });

  const projects = fetchedProjects 
    ? [...fetchedProjects].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    : [];

  const deleteMutation = useMutation({
    // FIX: Changed type to `Project` to match what `ProjectCard` passes to `onDelete`.
    mutationFn: async (project: Project) => {
      const projectIdentifier = project.uuid || project.id;
      return await apiService.delete(`/api/story-projects/${projectIdentifier}`);
    },
    // Optimistic update: remove project from UI immediately
    onMutate: async (deletingProject) => {
      // Cancel outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: ['/api/story-projects/summaries'] });

      // Snapshot the previous value
      const previousProjects = queryClient.getQueryData(['/api/story-projects/summaries']);

      // Optimistically update to remove the project
      queryClient.setQueryData(['/api/story-projects/summaries'], (old: any) => {
        if (!old) return [];
        return old.filter((p: any) =>
          p.id !== deletingProject.id && p.uuid !== deletingProject.uuid
        );
      });

      // Return a context object with the snapshotted value
      return { previousProjects };
    },
    onSuccess: () => {
      toast({ title: "Project Deleted", description: "The project has been successfully removed." });
      // Don't invalidate here since optimistic update already worked
    },
    onError: (error: any, deletingProject, context) => {
      // Handle 404 as success (project already deleted on another device/tab)
      if (error.message?.includes('404')) {
        toast({ title: "Project Deleted", description: "The project has been successfully removed." });
        return;
      }

      toast({ title: "Error", description: error.message, variant: "destructive" });

      // If the mutation fails, roll back to previous state
      queryClient.setQueryData(['/api/story-projects/summaries'], context?.previousProjects);
    },
    onSettled: () => {
      // Optimistic update is sufficient on success; nothing extra needed
    }
  });

  const generateBlueprintMutation = useMutation({
    mutationFn: async (idea: string) => {
      const requestData = { 
        idea,
        selectedLlmModel: selectedModel?.apiName
      };
      return await apiService.post("/api/story-projects/generate-blueprint", requestData);
    },
    onSuccess: (data) => {
      // Pre-populate cache with blueprint response to prevent redundant API calls
      if (data.projectId && data.data) {
        const projectSummary = {
          id: data.projectId,
          uuid: data.projectId,
          title: data.data.title || "Untitled Project",
          bookType: data.data.bookType || "NOVEL",
          targetWordCount: data.data.targetWordCount || 80000,
          hasBlueprint: true,
          progress: data.progress || {},
          idea: data.data.idea || "",
          projectData: data.data
        };

        cacheManager.set('project-summary', data.projectId, projectSummary, 5 * 60 * 1000);
        queryClient.setQueryData(['story-project-summary', data.projectId], projectSummary);

        if (data.data.blueprint) {
          const blueprintData = {
            exists: true,
            loaded: true,
            data: data.data.blueprint,
            error: null
          };
          cacheManager.setBlueprint(data.projectId, data.data.blueprint);
          queryClient.setQueryData(['project-blueprint', data.projectId], blueprintData);
        }

        // Prevent automatic refetches for 5 minutes
        queryClient.setQueryDefaults(['story-project-summary', data.projectId], {
          staleTime: 5 * 60 * 1000,
          refetchOnMount: false,
          refetchOnWindowFocus: false,
        });
        queryClient.setQueryDefaults(['project-blueprint', data.projectId], {
          staleTime: 5 * 60 * 1000,
          refetchOnMount: false,
          refetchOnWindowFocus: false,
        });
      }

      toast({
        title: "Blueprint Generated!",
        description: "Your new story project is ready. Redirecting..."
      });
      queryClient.invalidateQueries({ queryKey: ['/api/story-projects/summaries'] });
      setTimeout(() => {
        navigate(`/story-workspace/${data.projectId}`);
      }, 1500);
    },
    onError: (error: any) => {
      let errorMessage = error.message || "Failed to generate blueprint. Please try again.";

      // 400 errors often indicate model/provider issues
      if (error.message?.includes('400') || error.message?.includes('Bad Request')) {
        errorMessage = selectedModel
          ? `The selected model (${selectedModel.modelName}) is currently overloaded or unavailable. Please try selecting a different model.`
          : "The AI service is currently overloaded. Please select a different AI model and try again.";
      }

      toast({
        title: "Generation Failed",
        description: errorMessage,
        variant: "destructive"
      });
    },
  });

  const handleGenerateBlueprint = () => {
    if (!storyIdea.trim()) {
      toast({ title: "Error", description: "Please enter your story idea first.", variant: "destructive" });
      return;
    }
    generateBlueprintMutation.mutate(storyIdea);
  };

  
  return (
    <DashboardLayout>
      <div className="space-y-6 sm:space-y-8">
        <div className="text-center space-y-3 sm:space-y-4">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-primary to-purple-600 bg-clip-text text-transparent">
            Start Your Masterpiece- Dashboard
          </h1>
          <p className="text-sm sm:text-base lg:text-lg text-muted-foreground max-w-3xl mx-auto px-4">
            Give us your core idea. We'll generate a comprehensive blueprint with titles, characters, and world details to kickstart your creative journey.
          </p>
        </div>

      <Card className="w-full max-w-4xl mx-auto shadow-lg border-primary/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <Sparkles className="h-6 w-6 text-purple-500" />
            Your Core Idea
          </CardTitle>
          <CardDescription>Describe your story idea here to generate a new project blueprint.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Textarea
            placeholder="e.g., A young wizard discovers a hidden library that contains books from parallel universes, each book allowing the reader to temporarily live in that world..."
            value={storyIdea}
            onChange={(e) => setStoryIdea(e.target.value)}
            className="min-h-[120px] text-base focus:ring-2 focus:ring-primary"
          />
          
          {/* Model Selection Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <h3 className="text-sm font-medium">AI Model Selection</h3>
                <p className="text-xs text-muted-foreground">
                  Choose the AI model for blueprint generation
                  {selectedModel && ` • Current: ${selectedModel.modelName}`}
                </p>
              </div>
              <ModelSelector
                taskType="blueprint"
                onModelSelect={(model) => setSelectedModel(model)}
                baseCost={1000} // Base cost for blueprint generation
              >
                <Button variant="outline" size="sm">
                  {selectedModel ? `${selectedModel.modelName}` : 'Select Model'}
                </Button>
              </ModelSelector>
            </div>
            
            {!selectedModel && (
              <div className="text-xs text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
                ⚠️ No model selected. The system will use the default model, which may be overloaded.
              </div>
            )}
          </div>
          
          <div className="flex justify-end items-center">
            <Button 
              onClick={handleGenerateBlueprint}
              disabled={!storyIdea.trim() || generateBlueprintMutation.isPending}
              className="w-full sm:w-auto sm:min-w-[180px] h-12 sm:h-auto"
              size="lg"
            >
              {generateBlueprintMutation.isPending ? (
                <Zap className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="mr-2 h-4 w-4" />
              )}
              {generateBlueprintMutation.isPending ? 'Generating...' : 'Generate Project'}
              {selectedModel && !generateBlueprintMutation.isPending && (
                <span className="ml-2 text-xs opacity-80">
                  ({(1000 * selectedModel.creditMultiplier).toLocaleString()} credits)
                </span>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-xl sm:text-2xl font-semibold">Recent Projects</h2>
          <Link to="/my-stories" className="w-full sm:w-auto">
            <Button variant="outline" className="w-full sm:w-auto">View All</Button>
          </Link>
        </div>
        
        {projects.length > 0 ? (
          <div className="grid gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
            {projects.slice(0, 3).map((project) => (
              <ProjectCard
                key={project.id}
                project={project}
                onDelete={deleteMutation.mutate as (project: { id: number; uuid?: string }) => void}
                isDeleting={deleteMutation.isPending}
                variant="compact"
              />
            ))}
          </div>
        ) : (
          <Card className="text-center py-12 border-dashed">
            <CardContent>
              <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No projects yet</h3>
              <p className="text-muted-foreground">
                Use the idea generator above to start your first story!
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      <div className="grid gap-4 sm:gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Credits</CardTitle>
            <Zap className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{creditStats?.balance || 0}</div>
            <p className="text-xs text-muted-foreground">
              +{creditStats?.totalEarned || 0} earned this month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Usage</CardTitle>
            <TrendingUp className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{usageStats?.totalTasks || 0}</div>
            <p className="text-xs text-muted-foreground">
              {usageStats?.thisMonthTasks || 0} tasks this month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Referrals</CardTitle>
            <Users className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">0</div>
            <p className="text-xs text-muted-foreground">
              Share and earn credits
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Plan</CardTitle>
            <Award className="h-4 w-4 text-purple-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{user?.planCode || "FREE"}</div>
            <Link to="/upgrade" className="text-xs text-primary hover:underline">
              Upgrade plan
            </Link>
          </CardContent>
        </Card>
      </div>
      </div>
    </DashboardLayout>
  );
}